import { create } from 'zustand';
import type { AdminChapter } from '@/domain/model/AdminChapter';
import type { CreateChapterRequest } from '@/application/dto/CreateChapterRequest';
import type { UpdateChapterRequest } from '@/application/dto/UpdateChapterRequest';
import { adminService } from '@/infrastructure/store/adminServiceInstance';
import { resolveErrorKey } from '@/infrastructure/util/error';

interface AdminChapterState {
  chapters: AdminChapter[];
  isLoading: boolean;
  error: string | null;
}

interface AdminChapterActions {
  fetchChapters: (subcategoryId?: string) => Promise<void>;
  createChapter: (data: CreateChapterRequest) => Promise<boolean>;
  updateChapter: (id: string, data: UpdateChapterRequest) => Promise<boolean>;
  deleteChapter: (id: string) => Promise<boolean>;
  clearError: () => void;
}

export const useAdminChapterStore = create<AdminChapterState & AdminChapterActions>((set) => ({
  chapters: [],
  isLoading: false,
  error: null,

  fetchChapters: async (subcategoryId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const chapters = await adminService.getChapters(subcategoryId);
      set({ chapters, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.chapterLoadFailed') });
    }
  },

  createChapter: async (data: CreateChapterRequest) => {
    set({ isLoading: true, error: null });
    try {
      await adminService.createChapter(data);
      set({ isLoading: false });
      return true;
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.chapterSaveFailed') });
      return false;
    }
  },

  updateChapter: async (id: string, data: UpdateChapterRequest) => {
    set({ isLoading: true, error: null });
    try {
      await adminService.updateChapter(id, data);
      set({ isLoading: false });
      return true;
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.chapterSaveFailed') });
      return false;
    }
  },

  deleteChapter: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await adminService.deleteChapter(id);
      set({ isLoading: false });
      return true;
    } catch {
      set({ isLoading: false });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
